import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/features/vpn/components/Facts';
import { useVpnPermissions } from '@/features/vpn/permissions';
import { MAX_FLOW_RETENTION_DAYS } from '../api';
import { useSetFlowRetention, useVpnSettings } from '../hooks';

function RetentionForm({ days, isDefault }: { days: number; isDefault: boolean }) {
  const { canUpdateOrgVpn } = useVpnPermissions();
  const save = useSetFlowRetention();
  const [text, setText] = useState(String(days));
  const value = Number(text);
  const valid = Number.isInteger(value) && value >= 1 && value <= MAX_FLOW_RETENTION_DAYS;
  const blocked = canUpdateOrgVpn ? undefined : "Changing it takes vpn:update across the organization";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          aria-label="Days to keep flow records"
          className="w-28"
          inputMode="numeric"
          value={text}
          disabled={!!blocked}
          title={blocked}
          onChange={(e) => { setText(e.target.value); }}
        />
        <span className="text-sm text-muted-foreground">days{isDefault ? ' (the default)' : ''}</span>
        <Button
          variant="outline"
          disabled={!!blocked || !valid || value === days || save.isPending}
          onClick={() => { save.mutate(value); }}
        >
          Save
        </Button>
        {!isDefault ? (
          <Button variant="ghost" disabled={!!blocked || save.isPending} onClick={() => { save.mutate(null); }}>
            Use the default
          </Button>
        ) : null}
      </div>
      {!valid ? <p className="text-xs text-sev-1">From 1 to {MAX_FLOW_RETENTION_DAYS} days.</p> : null}
      <p className="text-xs text-muted-foreground">Shortening it deletes older records at the next daily pass.</p>
    </div>
  );
}

/** How long the organization keeps its VPN flow records. */
export function FlowRetentionCard() {
  const { data, isLoading, isError } = useVpnSettings();
  return (
    <Card
      title="Flow records"
      description="Every connection made through the VPN is recorded: when, to what, how much each way, and the gateway's port as the site saw it."
    >
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : isError || !data ? (
        <p className="text-xs text-muted-foreground">Couldn&rsquo;t load the retention.</p>
      ) : (
        // Keyed by what's saved, so a save resets the field.
        <RetentionForm key={data.flowRetentionDays} days={data.flowRetentionDays} isDefault={data.flowRetentionIsDefault} />
      )}
    </Card>
  );
}
